import * as inflection from 'inflection'

import log from 'utils/logger' // eslint-disable-line
import Session from '../actions/session'
import { generatePath } from 'utils/paths'

const v2_api = {
  activities: {
    all: '/api/v2/:username/activities',
    get: '/api/v2/:username/activities/:id',
    destroy: '/api/v2/:username/activities',
  },
  dashboards: {
    all: '/api/v2/:username/dashboards',
    create: ['/api/v2/:username/dashboards', ['dashboard']],
    get: '/api/v2/:username/dashboards/:id',
    replace: ['/api/v2/:username/dashboards/:id', ['dashboard']],
    destroy: '/api/v2/:username/dashboards/:id',
  },
  blocks: {
    all: '/api/v2/:username/dashboards/:dashboard_id/blocks',
    create: ['/api/v2/:username/dashboards/:dashboard_id/blocks', ['block']],
    get: '/api/v2/:username/dashboards/:dashboard_id/blocks/:id',
    replace: ['/api/v2/:username/dashboards/:dashboard_id/blocks/:id', ['block']],
    destroy: '/api/v2/:username/dashboards/:dashboard_id/blocks/:id',
  },
  data: {
    all: '/api/v2/:username/feeds/:feed_key/data',
    create: ['/api/v2/:username/feeds/:feed_key/data', ['datum']],
    get: '/api/v2/:username/feeds/:feed_key/data/:id',
    destroy: '/api/v2/:username/feeds/:feed_key/data/:id',
  },
  groups: {
    all: '/api/v2/:username/groups',
    create: ['/api/v2/:username/groups', ['group']],
    get: '/api/v2/:username/groups/:group_key',
    replace: ['/api/v2/:username/groups/:group_key', ['group']],
    destroy: '/api/v2/:username/groups/:group_key',
    add_feed: ['/api/v2/:username/groups/:group_key/add', ['feed_key']],
    remove_feed: ['/api/v2/:username/groups/:group_key/remove', ['feed_key']]
  },
  feeds: {
    all: '/api/v2/:username/feeds',
    create: ['/api/v2/:username/feeds', ['feed']],
    get: '/api/v2/:username/feeds/:feed_key',
    replace: ['/api/v2/:username/feeds/:feed_key', ['feed']],
    details: '/api/v2/:username/feeds/:feed_key/details',
    destroy: '/api/v2/:username/feeds/:feed_key'
  },
  triggers: {
    all: '/api/v2/:username/triggers',
    create: ['/api/v2/:username/triggers', ['trigger']],
    get: '/api/v2/:username/triggers/:id',
    replace: ['/api/v2/:username/triggers/:id', ['trigger']],
    destroy: '/api/v2/:username/triggers/:id'
  }
}

// Converts the above short-hand strings and array into objects with path generator functions:
// '/api/v2/:username/triggers' becomes ->
//   { url: ({username }) => `/api/v2/${username}/triggers` }
// [ '/api/v2/:username/triggers/:id', ['trigger'] ] becomes ->
//   { url: ({username, id }) => `/api/v2/${username}/triggers/${id}`, args: ['trigger'] }
Object.values(v2_api).forEach(endpoint => {
  Object.keys(endpoint).forEach(action => {
    if (typeof endpoint[action] === 'string') {
      endpoint[action] = {
        url: generatePath(endpoint[action])
      }
    } else {
      endpoint[action] = {
        url: generatePath(endpoint[action][0]),
        args: endpoint[action][1]
      }
    }
  })
})

export const buildAPIAction = (api, operation, cb) => {
  // log("[buildAPIAction]", { api, operation, cb })
  return (args={}, headers={}) => {
    return (dispatch, getState) => {
      const c = Session.getClient(dispatch, getState)

      if (!c) { return }

      args.username ||= c.username

      // allow overrides via callback
      if (cb) {
        return dispatch({
          type: `${api}_${operation}`.toUpperCase(),
          payload: cb.call(this, c, args)
        })
      }

      // URL
      let method = 'GET'
      switch(operation) {
        case 'create':
        case 'add_feed':
        case 'group_feed_create':
        case 'remove_feed':
          method = 'POST'
          break
        case 'destroy':
          method = 'DELETE'
          break
        case 'replace':
          method = 'PUT'
          break
        case 'update':
          method = 'PATCH'
          break
      }

      const
        endpoint = v2_api[api][operation],
        url = endpoint.url(args),
        options = {
          method,
          headers,
          includeHeaders: true
        }

      if (endpoint.args) {
        const body = endpoint.args.reduce((memo, value) => {
          memo[value] = args[value]
          return memo
        }, {})
        options.body = JSON.stringify(body)
      }

      return dispatch({
        type: `${api}_${operation}`.toUpperCase(),
        payload: Session.ajax(c, url, options).then(response => {
          return {
            object: response.body,
            headers: response.headers
          }
        })
      })
    }
  }
}

export const buildAPIActions = (name) => {
  const actions = {}
  const operations = v2_api[name] || {}

  Object.keys(operations).forEach(operation => {
    actions[operation] = buildAPIAction(name, operation)
  })

  actions.resetErrors = () => {
    const PLURAL = inflection.pluralize(name).toUpperCase()
    return {
      type: `${PLURAL}_RESET_ERRORS`,
      payload: null
    }
  }

  return actions
}
